import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Minus } from "lucide-react";
import { api, errCode } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useMaster, nameOf } from "../lib/master";
import { todayCL } from "../lib/time";
import { PageTitle, Sel } from "../components/Common";

const Counter = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-1">
    <button type="button" className="btn-secondary !min-h-[44px] !px-3" onClick={() => onChange(Math.max(0, value - 1))} data-testid={`${testid}-dec`}><Minus size={16} /></button>
    <input type="number" min={0} className="field !min-h-[44px] w-20 text-center font-mono" value={value} onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))} data-testid={testid} />
    <button type="button" className="btn-secondary !min-h-[44px] !px-3" onClick={() => onChange(value + 1)} data-testid={`${testid}-inc`}><Plus size={16} /></button>
  </div>
);

const SHIFT_HOURS = { day: 8, morning: 4, afternoon: 4 };
const EMPTY = { contractor_id: "", crew_id: "", shift: "day", planned: 0, actual: 0, hours: 8, alloc: {} };

export default function Attendance() {
  const { t, te } = useLang();
  const farms = useMaster("farms");
  const contractors = useMaster("contractors");
  const crews = useMaster("crews");
  const sectors = useMaster("sectors");
  const [date, setDate] = useState(todayCL());
  const [farmId, setFarmId] = useState("");
  const [f, setF] = useState(EMPTY);
  const [rows, setRows] = useState([]);
  const farm = farmId || farms[0]?.id;
  const load = () => farm && api.get("/attendance", { params: { date, farm_id: farm } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [date, farm]); // eslint-disable-line react-hooks/exhaustive-deps

  const farmObj = farms.find((x) => x.id === farm);
  const myContractors = contractors.filter((c) => c.grower_id === farmObj?.grower_id).map((c) => ({ ...c, name: `${c.code} · ${c.name}` }));
  const farmSectors = sectors.filter((s) => s.farm_id === farm);
  const allocated = Object.values(f.alloc).reduce((a, b) => a + b, 0);
  const over = allocated > f.actual;

  const save = async () => {
    try {
      await api.post("/attendance", { date, farm_id: farm, contractor_id: f.contractor_id, crew_id: f.crew_id || null, shift: f.shift,
        planned: f.planned, actual: f.actual, hours_per_picker: f.hours || null, allocations: Object.entries(f.alloc).filter(([, c]) => c > 0).map(([sector_id, count]) => ({ sector_id, count })) });
      toast.success(t("saved")); setF(EMPTY); load();
    } catch (e) { toast.error(te(errCode(e))); }
  };

  return (
    <div className="space-y-5" data-testid="attendance-page">
      <PageTitle>{t("attendance_title")}</PageTitle>
      <div className="card p-4 grid sm:grid-cols-3 gap-3">
        <label><span className="eyebrow">{t("date")}</span><input type="date" className="field mt-1" value={date} onChange={(e) => setDate(e.target.value)} data-testid="attendance-date-input" /></label>
        <Sel label={t("farm")} value={farm} onChange={setFarmId} options={farms} testid="attendance-farm-select" />
      </div>
      <div className="card p-4 space-y-4">
        <div className="grid sm:grid-cols-3 gap-3">
          <Sel label={t("contractor")} value={f.contractor_id} onChange={(v) => setF({ ...f, contractor_id: v, crew_id: "" })} options={myContractors} testid="attendance-contractor-select" placeholder={t("select")} />
          <Sel label={t("crew")} value={f.crew_id} onChange={(v) => setF({ ...f, crew_id: v })} options={crews.filter((c) => c.contractor_id === f.contractor_id && c.farm_id === farm)} testid="attendance-crew-select" />
          <label><span className="eyebrow">{t("shift")}</span>
            <select className="field mt-1" value={f.shift} onChange={(e) => setF({ ...f, shift: e.target.value, hours: SHIFT_HOURS[e.target.value] })} data-testid="attendance-shift-select">
              {["day", "morning", "afternoon"].map((s) => <option key={s} value={s}>{t(`shift_${s}`)}</option>)}</select></label>
        </div>
        <div className="flex flex-wrap gap-6">
          <div><p className="eyebrow mb-1">{t("planned")}</p><Counter value={f.planned} onChange={(v) => setF({ ...f, planned: v })} testid="attendance-planned" /></div>
          <div><p className="eyebrow mb-1">{t("actual")}</p><Counter value={f.actual} onChange={(v) => setF({ ...f, actual: v })} testid="attendance-actual" /></div>
          <label><span className="eyebrow">{t("hours_per_picker")}</span>
            <input type="number" min={0.5} max={16} step={0.5} className="field !min-h-[44px] w-28 mt-1 font-mono" value={f.hours}
              onChange={(e) => setF({ ...f, hours: e.target.value === "" ? "" : Number(e.target.value) })} data-testid="attendance-hours-input" /></label>
        </div>
        <div>
          <p className="eyebrow mb-2">{t("allocations")} · {t("allocated")}: <span className={`font-mono ${over ? "text-red-700" : ""}`} data-testid="attendance-allocated-total">{allocated} / {f.actual}</span></p>
          <div className="grid sm:grid-cols-3 gap-3">
            {farmSectors.map((s) => (
              <div key={s.id}><p className="text-sm font-semibold mb-1">{s.name}</p>
                <Counter value={f.alloc[s.id] || 0} onChange={(v) => setF({ ...f, alloc: { ...f.alloc, [s.id]: v } })} testid={`attendance-alloc-${s.code}`} /></div>
            ))}
          </div>
          {over && <p className="text-sm font-bold text-red-700 mt-2" data-testid="attendance-over-error">{t("alloc_exceeds")}</p>}
        </div>
        <button className="btn-primary w-full sm:w-auto" disabled={!f.contractor_id || over} onClick={save} data-testid="attendance-save-button">{t("save")}</button>
      </div>
      <div className="card overflow-x-auto">
        <table className="tbl w-full" data-testid="attendance-table">
          <thead><tr><th>{t("contractor")}</th><th>{t("crew")}</th><th>{t("shift")}</th><th>{t("planned")}</th><th>{t("actual")}</th><th>{t("hours_per_picker")}</th><th>{t("allocations")}</th><th></th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id}><td>{nameOf(contractors, r.contractor_id)}</td><td>{nameOf(crews, r.crew_id)}</td><td>{t(`shift_${r.shift}`)}</td>
              <td className="font-mono">{r.planned}</td><td className="font-mono">{r.actual}</td><td className="font-mono">{r.hours_per_picker ?? "—"}</td>
              <td className="text-xs">{r.allocations.map((a) => `${nameOf(sectors, a.sector_id)}: ${a.count}`).join(" · ")}</td>
              <td><button className="text-sm underline" onClick={() => setF({ contractor_id: r.contractor_id, crew_id: r.crew_id || "", shift: r.shift, planned: r.planned, actual: r.actual, hours: r.hours_per_picker ?? SHIFT_HOURS[r.shift] ?? 8, alloc: Object.fromEntries(r.allocations.map((a) => [a.sector_id, a.count])) })}>{t("edit")}</button></td></tr>
          ))}{!rows.length && <tr><td colSpan={8} className="text-slate-500">{t("none")}</td></tr>}</tbody>
        </table>
      </div>
    </div>
  );
}
